// src/pages/master/ClientList.js
// Premium light UI — teal/slate theme
// Fixed: company_id filter added

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../../firebase";
import {
  collection, getDocs, doc, deleteDoc,
  query, where,
} from "firebase/firestore";
export default function ClientList() {
  const navigate   = useNavigate();
  const user       = JSON.parse(localStorage.getItem("user") || "{}");
  const company_id = user.company_id || "";

  const [clients,         setClients]         = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [sitesByClient,   setSitesByClient]   = useState({}); // client_id -> [site docs]
  const [loading,         setLoading]         = useState(true);
  const [error,           setError]           = useState("");
  const [search,          setSearch]          = useState("");
  const [expandedClient,  setExpandedClient]  = useState(null);
  const fetchClients = async () => {
    setLoading(true);
    setError("");
    try {
      // Query 1: clients
      const clientSnap = await getDocs(
        query(collection(db, "clients"), where("company_id", "==", company_id))
      );
      const clientData = clientSnap.docs.map(d => ({ _id: d.id, ...d.data() }));

      // Query 2: sites — SOT for sites, single query, group client-side (O(1) map)
      const siteSnap = await getDocs(
        query(collection(db, "sites"), where("company_id", "==", company_id))
      );
      const siteMap = {};
      siteSnap.docs.forEach(d => {
        const site = { _id: d.id, ...d.data() };
        const cid = site.client_id;
        if (!cid) return;
        if (!siteMap[cid]) siteMap[cid] = [];
        siteMap[cid].push(site);
      });

      setSitesByClient(siteMap);
      setClients(clientData);
      setFilteredClients(clientData);
    } catch (err) {
      console.error(err);
      setError("Failed to load clients");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchClients(); }, []); // eslint-disable-line

  useEffect(() => {
    const term = search.toLowerCase();
    setFilteredClients(clients.filter(c =>
      c.client_name?.toLowerCase().includes(term) ||
      c.contact?.toLowerCase().includes(term) ||
      (sitesByClient[c._id] || []).some(s =>
        s.site_name?.toLowerCase().includes(term) ||
        s.address?.toLowerCase().includes(term)
      )
    ));
  }, [search, clients, sitesByClient]);

  const deleteClient = async (clientId) => {
    if (!window.confirm("Delete this client and all their sites?")) return;
    try {
      await deleteDoc(doc(db, "clients", clientId));
      fetchClients();
    } catch (err) { alert("Delete failed"); }
  };

  const deleteSite = async (siteId) => {
    if (!window.confirm("Delete this site?")) return;
    try {
      await deleteDoc(doc(db, "sites", siteId));
      fetchClients();
    } catch (err) { alert("Site delete failed"); }
  };
  return (
    <div className="max-w-5xl mx-auto p-4 space-y-5">

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Client List</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {filteredClients.length} client{filteredClients.length !== 1 ? "s" : ""}
            {search && ` matching "${search}"`}
          </p>
        </div>
        <div className="flex gap-2">
          <div className="relative flex-1 md:w-72">
            <input type="text"
              placeholder="Search client, site, location..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full border border-slate-200 rounded-xl pl-4 pr-9 py-2.5 text-sm outline-none focus:ring-2 focus:ring-teal-400 bg-white shadow-sm" />
            {search && (
              <button onClick={() => setSearch("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs">
                ✕
              </button>
            )}
          </div>
          <button onClick={() => navigate("/master/client")}
            className="px-4 py-2.5 bg-teal-600 text-white rounded-xl text-sm font-semibold hover:bg-teal-700 transition shadow-sm whitespace-nowrap">
            + New Client
          </button>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="space-y-3">
          {[1,2,3].map(i => (
            <div key={i} className="h-20 bg-white rounded-2xl animate-pulse border border-slate-100" />
          ))}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
          {error}
        </div>
      )}

      {/* Empty */}
      {!loading && !error && filteredClients.length === 0 && (
        <div className="text-center py-16">
          <div className="text-4xl mb-3">👤</div>
          <p className="text-slate-500 font-medium">No clients found</p>
          {search && (
            <button onClick={() => setSearch("")}
              className="mt-2 text-teal-600 text-sm hover:underline">
              Clear search
            </button>
          )}
          {!search && (
            <button onClick={() => navigate("/master/client")}
              className="mt-3 px-4 py-2 bg-teal-600 text-white rounded-xl text-sm font-semibold">
              + Add First Client
            </button>
          )}
        </div>
      )}

      {/* Client Cards */}
      <div className="space-y-3">
        {filteredClients.map(client => {
          const isExpanded = expandedClient === client._id;
          return (
            <div key={client._id}
              className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden transition-all">

              {/* Collapsed header */}
              <div className="px-5 py-4 cursor-pointer flex items-center justify-between hover:bg-slate-50 transition"
                onClick={() => setExpandedClient(isExpanded ? null : client._id)}>
                <div className="flex items-center gap-3">
                  {/* Avatar */}
                  <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center shrink-0">
                    <span className="text-teal-700 font-bold text-base">
                      {(client.client_name || "?")[0].toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-800 text-base leading-tight">
                      {client.client_name || "Unnamed Client"}
                    </h3>
                    <div className="flex items-center gap-3 mt-0.5">
                      {client.contact && (
                        <span className="text-xs text-slate-400">📞 {client.contact}</span>
                      )}
                      <span className="text-xs font-semibold text-teal-600">
                        📍 {(sitesByClient[client._id]?.length || 0)} Site{(sitesByClient[client._id]?.length || 0) !== 1 ? "s" : ""}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
  <button
    onClick={e => { e.stopPropagation(); navigate(`/master/edit-client/${client._id}`); }}
    className="text-xs text-blue-600 hover:text-blue-800 font-semibold px-3 py-1.5 bg-blue-50 rounded-lg transition">
    Edit
  </button>
  <button
    onClick={e => { e.stopPropagation(); navigate(`/master/edit-client/${client._id}?addSite=true`); }}
    className="text-xs text-teal-600 hover:text-teal-800 font-semibold px-3 py-1.5 bg-teal-50 rounded-lg transition">
    + Add Site
  </button>
  <span className={`text-slate-400 text-sm transition-transform ${isExpanded ? "rotate-180" : ""}`}>
    ▾
  </span>
</div>
              </div>

              {/* Expanded */}
              {isExpanded && (
                <div className="border-t border-slate-100 px-5 pb-5">

                  {/* Client actions */}
                  <div className="flex gap-2 pt-4 pb-3 flex-wrap">
                    <button
                      onClick={() => navigate(`/master/edit-client/${client._id}`)}
                      className="px-3 py-2 bg-blue-50 text-blue-700 rounded-xl text-xs font-semibold hover:bg-blue-100 transition">
                      ✏️ Edit Client
                    </button>
                    <button
                      onClick={() => navigate(`/master/edit-client/${client._id}?addSite=true`)}
                      className="px-3 py-2 bg-teal-50 text-teal-700 rounded-xl text-xs font-semibold hover:bg-teal-100 transition">
                      + Add Site
                    </button>
                    <button
                      onClick={() => deleteClient(client._id)}
                      className="px-3 py-2 bg-red-50 text-red-600 rounded-xl text-xs font-semibold hover:bg-red-100 transition">
                      🗑 Delete Client
                    </button>
                  </div>

                 {/* Sites grid — SOT: sites collection, grouped by client_id */}
                  {(sitesByClient[client._id]?.length > 0) ? (
                    <div className="grid md:grid-cols-2 gap-3 mt-1">
                      {sitesByClient[client._id].map((site) => (
                        <div key={site._id}
                          className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="text-lg">📍</span>
                              <h4 className="font-bold text-slate-800 text-sm">
                                {site.site_name}
                              </h4>
                            </div>
                          </div>
                          {site.address && (
                            <p className="text-xs text-slate-500 mb-1">{site.address}</p>
                          )}
                          {site.location_link && (
                            <a href={site.location_link} target="_blank" rel="noreferrer"
                              className="text-xs text-teal-600 underline font-semibold">
                              Open Map →
                            </a>
                          )}
                          <div className="flex gap-2 mt-3">
                            <button
                              onClick={() => navigate(`/master/edit-client/${client._id}?siteId=${site._id}`)}
                              className="px-3 py-1.5 bg-amber-50 text-amber-700 rounded-lg text-xs font-semibold hover:bg-amber-100 transition">
                              Edit
                            </button>
                            <button
                              onClick={() => deleteSite(site._id)}
                              className="px-3 py-1.5 bg-red-50 text-red-600 rounded-lg text-xs font-semibold hover:bg-red-100 transition">
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <p className="text-slate-400 text-sm">No sites added yet</p>
                      <button
                        onClick={() => navigate(`/master/edit-client/${client._id}?addSite=true`)}
                        className="mt-2 text-teal-600 text-xs hover:underline font-semibold">
                        + Add Site
                      </button>
                    </div>
                  )}                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}